import { useEffect, useMemo, useRef, useState } from "react";
import { Download, FileText, PieChart } from "lucide-react";
import { api } from "../api";
import { ErrorBlock, LoadingBlock, PageHead } from "../shell";
import { PortfolioFilters, emptyFilters } from "./portfolio/PortfolioFilters";
import { MetricSwitch, PortfolioKpis, WarningBand } from "./portfolio/PortfolioSummary";
import { CompanyDrawer, CustomerDrawer } from "./portfolio/PortfolioDrawer";
import { BranchesTab, CustomersTab, InsurersTab, MatrixTab, OrganizationTab } from "./portfolio/PortfolioTabs";
import "./PortfolioAnalysis.css";
import { can } from "../lib/permissions";

const TABS = [
  ["insurers", "Sigorta Şirketleri", "/portfolio/insurers"],
  ["branches", "Branşlar & Poliçe Grupları", "/portfolio/policy-groups"],
  ["customers", "Müşteriler", "/portfolio/customers"],
  ["organization", "Organizasyon", "/portfolio/organization"],
  ["matrix", "Branş × Sigorta Şirketi", "/portfolio/matrix"],
];

export default function PortfolioAnalysis({ user, dataset, periodProps }) {
  const [filters, setFilters] = useState(emptyFilters);
  const [metric, setMetric] = useState("net_premium");
  const [activeTab, setActiveTab] = useState("insurers");
  const [summary, setSummary] = useState(null);
  const [tabData, setTabData] = useState(null);
  const [crossData, setCrossData] = useState(null);
  const [error, setError] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [exporting, setExporting] = useState(null);
  const sequence = useRef(0);
  const requestParams = useMemo(() => ({ ...periodProps.periodParams, ...filters, metric }), [periodProps.periodParams, filters, metric]);
  const requestKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  useEffect(() => {
    const controller = new AbortController(); const request = ++sequence.current;
    setSummary(null); setError(null); setDrawer(null);
    api.get("/portfolio/summary", { params: requestParams, signal: controller.signal })
      .then((response) => { if (request === sequence.current) setSummary({ key: requestKey, data: response.data }); })
      .catch((problem) => { if (problem?.code !== "ERR_CANCELED" && request === sequence.current) setError(problem.response?.data?.detail || "Portföy özeti yüklenemedi."); });
    return () => controller.abort();
  }, [requestKey, requestParams]);

  useEffect(() => {
    if (!summary || summary.key !== requestKey) { setTabData(null); setCrossData(null); return; }
    const controller = new AbortController(); const endpoint = TABS.find(([key]) => key === activeTab)?.[2];
    setTabData(null); setCrossData(null);
    const requests = [api.get(endpoint, { params: requestParams, signal: controller.signal })];
    if (activeTab === "matrix") requests.push(api.get("/portfolio/cross-analysis", { params: requestParams, signal: controller.signal }));
    Promise.all(requests).then(([main, cross]) => { setTabData({ tab: activeTab, key: requestKey, data: main.data }); if (cross) setCrossData(cross.data); })
      .catch((problem) => { if (problem?.code !== "ERR_CANCELED") setError(problem.response?.data?.detail || "Analiz tablosu yüklenemedi."); });
    return () => controller.abort();
  }, [activeTab, requestKey, requestParams, summary]);

  const openCompany = async (id) => {
    setDrawer({ type: "loading" });
    try { const response = await api.get(`/portfolio/insurers/${id}`, { params: requestParams }); setDrawer({ type: "company", data: response.data }); }
    catch (problem) { setDrawer(null); setError(problem.response?.data?.detail || "Şirket detayı yüklenemedi."); }
  };
  const openCustomer = async (id) => {
    setDrawer({ type: "loading" });
    try { const response = await api.get(`/portfolio/customers/${id}`, { params: requestParams }); setDrawer({ type: "customer", data: response.data }); }
    catch (problem) { setDrawer(null); setError(problem.response?.data?.detail || "Müşteri detayı yüklenemedi."); }
  };
  const download = async (type) => {
    setExporting(type);
    try {
      const response = await api.get(`/portfolio/export/${type}`, { params: requestParams, responseType: "blob" });
      const url = URL.createObjectURL(response.data); const anchor = document.createElement("a");
      const match = response.headers["content-disposition"]?.match(/filename="?([^";]+)"?/);
      anchor.href = url; anchor.download = match?.[1] || `IMC_Portfoy_Analizi.${type === "excel" ? "xlsx" : "pdf"}`; anchor.click();
      URL.revokeObjectURL(url);
    } catch (problem) { setError(problem.response?.data?.detail || "Rapor indirilemedi."); }
    finally { setExporting(null); }
  };
  const currentTabData = tabData?.tab === activeTab && tabData?.key === requestKey ? tabData.data : null;

  return <main className="content portfolio-page">
    <PageHead eyebrow="YÖNETİM ANALİTİĞİ" title="Portföy Analizi" desc="Yoğunlaşma riskleri, şirket-branş gücü ve müşteri bağımlılığı."
      actions={can(user,"portfolio.export")?<div className="portfolio-export-actions"><button data-testid="export-excel-btn" className="outline-button" disabled={!!exporting} onClick={() => download("excel")}><Download size={14}/>{exporting === "excel" ? "Hazırlanıyor" : "Excel"}</button><button data-testid="export-pdf-btn" className="primary-button" disabled={!!exporting} onClick={() => download("pdf")}><FileText size={14}/>{exporting === "pdf" ? "Hazırlanıyor" : "PDF Yönetim Raporu"}</button></div>:null} />
    <PortfolioFilters dataset={dataset} periodProps={periodProps} filters={filters} setFilters={setFilters}/>
    <div className="portfolio-metric-row"><div><PieChart size={17}/><span>Yoğunlaşma metriği</span></div><MetricSwitch metric={metric} setMetric={setMetric}/></div>
    {error && <ErrorBlock message={error}/>} {!summary && !error && <LoadingBlock label="Portföy yoğunlaşması hesaplanıyor..."/>}
    {summary?.data && <><PortfolioKpis data={summary.data}/><WarningBand warnings={summary.data.warnings}/></>}
    <div className="portfolio-tabs" data-testid="portfolio-analysis-tabs">{TABS.map(([key, label]) => <button data-testid={`tab-${key}`} key={key} className={activeTab === key ? "active" : ""} onClick={() => setActiveTab(key)}>{label}</button>)}</div>
    <section className="panel portfolio-analysis-panel">
      {!currentTabData ? <LoadingBlock label="Analiz tablosu hazırlanıyor..."/> : <>
        {activeTab === "insurers" && <InsurersTab data={currentTabData} onCompany={openCompany}/>} 
        {activeTab === "branches" && <BranchesTab data={currentTabData} requestParams={requestParams}/>} 
        {activeTab === "customers" && <CustomersTab data={currentTabData} onCustomer={openCustomer}/>} 
        {activeTab === "organization" && <OrganizationTab data={currentTabData} onNode={(id) => setFilters({ ...filters, org_node_id: id })}/>} 
        {activeTab === "matrix" && <MatrixTab data={currentTabData} cross={crossData} onCompany={openCompany}/>} 
      </>}
    </section>
    {drawer?.type === "company" && <CompanyDrawer data={drawer.data} onClose={() => setDrawer(null)}/>} 
    {drawer?.type === "customer" && <CustomerDrawer data={drawer.data} onClose={() => setDrawer(null)}/>} 
    {drawer?.type === "loading" && <div data-testid="portfolio-detail-loading" className="portfolio-drawer-backdrop"><div className="loading">Detay hazırlanıyor...</div></div>}
  </main>;
}